import { useContext, useEffect } from 'react';
import { AuthContext } from '@/providers/AuthProvider';
import { RoleName } from '@/api/entities/user-role-type';
import { TLoginSchema } from '@/schemas/auth/login.schema';
import { TCustomQueryProps } from '@/type/hooks.types';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  logIn,
  logInByUserId,
  resetPassword,
  sendEmailRestPassword,
} from '@/api/auth/auth';
import { ErrorResponse } from '@/utils/axios.http';
import { TForgotPasswordSchema } from '@/schemas/auth/forgot-password.schema';
import { useLocation } from 'react-router-dom';

export const useLogin = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: TLoginSchema) => logIn(data),
    onSuccess: (data) => {
      // Invalidate all queries to ensure fresh data for the new user session
      queryClient.invalidateQueries();
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      handleError?.(error);
      console.error('Error when logging in:', error.error);
    },
  });
};

export const useLoginByUserId = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (userId: string) => logInByUserId(userId),
    onSuccess: (data) => {
      // Clear all cached queries — a full page reload follows, so no need to refetch
      queryClient.clear();
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      handleError?.(error);
      console.error('Error when logging in by user id:', error.error);
    },
  });
};

export const useSendEmailRestPassword = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: (data: TForgotPasswordSchema) =>
      sendEmailRestPassword({ recipient: data.email }),
    onSuccess: (data) => {
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      handleError?.(error);
      console.error('Error when sending email to reset password:', error.error);
    },
  });
};

export const useResetPassword = ({
  handleSuccess,
  handleError,
}: Partial<TCustomQueryProps> = {}) => {
  return useMutation({
    mutationFn: (data: { token: string; password: string }) =>
      resetPassword({ token: data?.token, password: data?.password }),
    onSuccess: (data) => {
      handleSuccess?.(data);
    },
    onError: (error: ErrorResponse) => {
      handleError?.(error);
      console.error('Error when resetting password:', error.error);
    },
  });
};

export const useAuth = () => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }

  return context;
};

export const useHideThemeOnInit = () => {
  const { loggedInUser } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Always show document for public pages
    if (
      location.pathname === '/login' ||
      location.pathname === '/register' ||
      location.pathname === '/forgot-password' ||
      location.pathname.startsWith('/reset-password') ||
      location.pathname.startsWith('/vehicule-check/view/') ||
      location.pathname.startsWith('/vehicule-check-request/view/')
    ) {
      document.documentElement.classList.remove('hidden');
      return;
    }

    // Hide document only for protected pages when user is NOT logged in
    if (!loggedInUser) {
      document.documentElement.classList.add('hidden');
    } else {
      document.documentElement.classList.remove('hidden');
    }
  }, [loggedInUser, location.pathname]);

  return { loggedInUser };
};

export const useUserRole = () => {
  const { primaryRole } = useAuth();

  const hasNoRole = !primaryRole;

  const isAdmin = primaryRole === RoleName.ADMIN;
  const isModerator = primaryRole === RoleName.MODERATOR;
  const isGroupManager = primaryRole === RoleName.GROUP_MANAGER;
  const isCompanyManager = primaryRole === RoleName.COMPANY_MANAGER;
  const isBranchManager = primaryRole === RoleName.BRANCH_MANAGER;
  const isBranchAgent = primaryRole === RoleName.BRANCH_AGENT;

  return {
    hasNoRole,
    isAdmin,
    isModerator,
    isGroupManager,
    isCompanyManager,
    isBranchManager,
    isBranchAgent,
  };
};
